import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { sans, mono, green, greenLight, useTheme } from '../../tokens';

const inputStyle = (t) => ({
  fontFamily: sans,
  fontSize: 12,
  padding: '8px 12px',
  border: '1px solid ' + t.border,
  borderRadius: 6,
  background: t.inputBg,
  color: t.textPrimary,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

const labelStyle = (t) => ({
  fontSize: 11,
  fontWeight: 600,
  color: t.textSecondary,
  fontFamily: sans,
  marginBottom: 6,
  display: 'block',
});

const sectionTitleStyle = (t) => ({
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: t.textMuted,
  fontFamily: sans,
  marginBottom: 16,
  paddingBottom: 8,
  borderBottom: '1px solid ' + t.border,
});

const toggleStyle = (enabled, t) => ({
  width: 36,
  height: 20,
  borderRadius: 10,
  background: enabled ? green : t.border,
  position: 'relative',
  cursor: 'pointer',
  transition: 'background 0.15s',
  flexShrink: 0,
});

const toggleKnob = (enabled) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: '#fff',
  position: 'absolute',
  top: 2,
  left: enabled ? 18 : 2,
  transition: 'left 0.15s',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
});

const INITIAL_CLASSES = [
  { id: 1, name: 'Common Shares', type: 'common', authorized: 10000000, issued: 4500000, active: true },
  { id: 2, name: 'Series Seed Preferred', type: 'preferred', authorized: 2000000, issued: 1800000, active: true },
  { id: 3, name: 'Series A Preferred', type: 'preferred', authorized: 3000000, issued: 2500000, active: true },
  { id: 4, name: 'Series A-2 Preferred', type: 'preferred', authorized: 1500000, issued: 500000, active: true },
];

export default function EquitySettings() {
  const t = useTheme();
  const savedEquity = JSON.parse(localStorage.getItem('finkloz_equity_settings') || 'null');
  const [classes, setClasses] = useState(savedEquity?.classes || INITIAL_CLASSES);
  const [showAddClass, setShowAddClass] = useState(false);
  const [newClass, setNewClass] = useState({ name: '', type: 'common', authorized: '', issued: '' });
  const [optionDefaults, setOptionDefaults] = useState(savedEquity?.optionDefaults || {
    poolSize: '1500000',
    vestingYears: '4',
    cliffMonths: '12',
    frequency: 'monthly',
    priceMethod: 'fmv',
    expiryYears: '10',
    terminationExercise: '90',
    terminationCause: '0',
  });
  const [spcc, setSpcc] = useState(savedEquity?.spcc || {
    warningThreshold: '55',
    alertEnabled: true,
  });
  const [sbc, setSbc] = useState(savedEquity?.sbc || {
    model: 'black-scholes',
    riskFreeRate: '3.50',
    volatility: '65.00',
    dividendYield: '0.00',
    expectedTerm: '6.25',
  });
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const markDirty = () => setDirty(true);

  const handleSave = () => {
    localStorage.setItem('finkloz_equity_settings', JSON.stringify({
      classes, optionDefaults, spcc, sbc,
    }));
    setSaved(true);
    setDirty(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleAddClass = () => {
    if (!newClass.name || !newClass.authorized) return;
    setClasses(prev => [...prev, {
      id: Date.now(),
      ...newClass,
      authorized: parseInt(newClass.authorized) || 0,
      issued: parseInt(newClass.issued) || 0,
      active: true,
    }]);
    setNewClass({ name: '', type: 'common', authorized: '', issued: '' });
    setShowAddClass(false);
    markDirty();
  };

  const fmt = (n) => Number(n).toLocaleString();

  return (
    <div style={{ maxWidth: 800 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, margin: '0 0 4px' }}>
        Equity Settings
      </h2>
      <p style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, margin: '0 0 24px' }}>
        Share classes, option plan defaults, SPCC thresholds, and SBC assumptions.
      </p>

      {/* Share Classes */}
      <div style={sectionTitleStyle(t)}>Share Classes</div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button
          onClick={() => setShowAddClass(!showAddClass)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', border: 'none', borderRadius: 6,
            background: green, color: '#fff', fontSize: 12, fontWeight: 600,
            fontFamily: sans, cursor: 'pointer',
          }}
        >
          <Plus size={14} />
          Add Class
        </button>
      </div>

      {showAddClass && (
        <div style={{
          background: t.bgSurface, border: '1px solid ' + t.border,
          borderRadius: 8, padding: 16, marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: sans, marginBottom: 12 }}>
            Add Share Class
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={labelStyle(t)}>Class Name *</label>
              <input
                type="text"
                value={newClass.name}
                onChange={e => setNewClass(p => ({ ...p, name: e.target.value }))}
                style={inputStyle(t)}
                placeholder="Series B Preferred"
              />
            </div>
            <div>
              <label style={labelStyle(t)}>Type</label>
              <select
                value={newClass.type}
                onChange={e => setNewClass(p => ({ ...p, type: e.target.value }))}
                style={inputStyle(t)}
              >
                <option value="common">Common</option>
                <option value="preferred">Preferred</option>
              </select>
            </div>
            <div>
              <label style={labelStyle(t)}>Authorized *</label>
              <input
                type="number"
                value={newClass.authorized}
                onChange={e => setNewClass(p => ({ ...p, authorized: e.target.value }))}
                style={inputStyle(t)}
                placeholder="5000000"
              />
            </div>
            <div>
              <label style={labelStyle(t)}>Issued</label>
              <input
                type="number"
                value={newClass.issued}
                onChange={e => setNewClass(p => ({ ...p, issued: e.target.value }))}
                style={inputStyle(t)}
                placeholder="0"
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAddClass} style={{
                padding: '8px 14px', border: 'none', borderRadius: 6,
                background: green, color: '#fff', fontSize: 12, fontWeight: 600,
                fontFamily: sans, cursor: 'pointer',
              }}>
                Add
              </button>
              <button onClick={() => setShowAddClass(false)} style={{
                padding: '8px 10px', border: '1px solid ' + t.border, borderRadius: 6,
                background: 'transparent', color: t.textSecondary, fontSize: 12,
                fontFamily: sans, cursor: 'pointer',
              }}>
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{
        border: '1px solid ' + t.border,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 32,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 12 }}>
          <thead>
            <tr style={{ background: t.bgSurface }}>
              {['Class Name', 'Type', 'Authorized', 'Issued', 'Status'].map(h => (
                <th key={h} style={{
                  padding: '10px 14px',
                  textAlign: h === 'Authorized' || h === 'Issued' ? 'right' : 'left',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: t.textMuted,
                  borderBottom: '1px solid ' + t.border,
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {classes.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid ' + t.borderLight }}>
                <td style={{ padding: '10px 14px', color: t.textPrimary, fontWeight: 500 }}>
                  {c.name}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: 0.3,
                    background: c.type === 'preferred' ? '#E3F2FD' : t.pillBg,
                    color: c.type === 'preferred' ? '#1565C0' : t.textSecondary,
                  }}>
                    {c.type}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: mono, fontSize: 11, color: t.textPrimary }}>
                  {fmt(c.authorized)}
                </td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: mono, fontSize: 11, color: t.textPrimary }}>
                  {fmt(c.issued)}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                    background: c.active ? greenLight : '#FCEBEB',
                    color: c.active ? green : '#C0392B',
                  }}>
                    {c.active ? 'active' : 'inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Option Plan Defaults */}
      <div style={sectionTitleStyle(t)}>Option Plan Defaults</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle(t)}>Option Pool Size (shares)</label>
          <input
            type="text"
            value={optionDefaults.poolSize}
            onChange={e => { setOptionDefaults(p => ({ ...p, poolSize: e.target.value })); markDirty(); }}
            style={inputStyle(t)}
          />
        </div>
        <div>
          <label style={labelStyle(t)}>Vesting Period (years)</label>
          <select
            value={optionDefaults.vestingYears}
            onChange={e => { setOptionDefaults(p => ({ ...p, vestingYears: e.target.value })); markDirty(); }}
            style={inputStyle(t)}
          >
            {['2', '3', '4', '5'].map(y => (
              <option key={y} value={y}>{y} years</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle(t)}>Cliff (months)</label>
          <select
            value={optionDefaults.cliffMonths}
            onChange={e => { setOptionDefaults(p => ({ ...p, cliffMonths: e.target.value })); markDirty(); }}
            style={inputStyle(t)}
          >
            {['0', '6', '12', '18', '24'].map(m => (
              <option key={m} value={m}>{m} months</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle(t)}>Vesting Frequency</label>
          <select
            value={optionDefaults.frequency}
            onChange={e => { setOptionDefaults(p => ({ ...p, frequency: e.target.value })); markDirty(); }}
            style={inputStyle(t)}
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annually">Annually</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Exercise Price Methodology</label>
        <div style={{ display: 'flex', gap: 16 }}>
          {[
            { value: 'fmv', label: 'Fair Market Value' },
            { value: 'board', label: 'Board-determined' },
            { value: '409a', label: '409A Valuation' },
          ].map(opt => (
            <label key={opt.value} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
            }}>
              <input
                type="radio"
                name="priceMethod"
                value={opt.value}
                checked={optionDefaults.priceMethod === opt.value}
                onChange={() => { setOptionDefaults(p => ({ ...p, priceMethod: opt.value })); markDirty(); }}
                style={{ accentColor: green }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 32 }}>
        <div>
          <label style={labelStyle(t)}>Expiry Default (years)</label>
          <select
            value={optionDefaults.expiryYears}
            onChange={e => { setOptionDefaults(p => ({ ...p, expiryYears: e.target.value })); markDirty(); }}
            style={inputStyle(t)}
          >
            {['5', '7', '10'].map(y => (
              <option key={y} value={y}>{y} years</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle(t)}>Post-Termination Exercise (days)</label>
          <input
            type="text"
            value={optionDefaults.terminationExercise}
            onChange={e => { setOptionDefaults(p => ({ ...p, terminationExercise: e.target.value })); markDirty(); }}
            style={inputStyle(t)}
          />
        </div>
        <div>
          <label style={labelStyle(t)}>Termination for Cause (days)</label>
          <input
            type="text"
            value={optionDefaults.terminationCause}
            onChange={e => { setOptionDefaults(p => ({ ...p, terminationCause: e.target.value })); markDirty(); }}
            style={inputStyle(t)}
          />
        </div>
      </div>

      {/* SPCC Settings */}
      <div style={sectionTitleStyle(t)}>SPCC Settings</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={labelStyle(t)}>Warning Threshold (%)</label>
          <input
            type="text"
            value={spcc.warningThreshold}
            onChange={e => { setSpcc(p => ({ ...p, warningThreshold: e.target.value })); markDirty(); }}
            style={{ ...inputStyle(t), maxWidth: 120 }}
          />
        </div>
        <div>
          <label style={labelStyle(t)}>Critical Threshold (%)</label>
          <div style={{
            padding: '8px 12px', fontSize: 12, fontFamily: mono,
            color: '#C0392B', fontWeight: 600,
          }}>
            50%
          </div>
          <div style={{ fontSize: 10, color: t.textMuted, fontFamily: sans }}>
            Fixed by CRA. Non-SPCC if non-resident ownership exceeds 50%.
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
        <div
          onClick={() => { setSpcc(p => ({ ...p, alertEnabled: !p.alertEnabled })); markDirty(); }}
          style={toggleStyle(spcc.alertEnabled, t)}
        >
          <div style={toggleKnob(spcc.alertEnabled)} />
        </div>
        <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: sans }}>
          Enable threshold alerts
        </span>
      </div>

      {/* SBC Settings */}
      <div style={sectionTitleStyle(t)}>Stock-Based Compensation</div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Valuation Model</label>
        <select
          value={sbc.model}
          onChange={e => { setSbc(p => ({ ...p, model: e.target.value })); markDirty(); }}
          style={{ ...inputStyle(t), maxWidth: 300 }}
        >
          <option value="black-scholes">Black-Scholes</option>
          <option value="binomial">Binomial Lattice</option>
        </select>
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: t.textSecondary, fontFamily: sans, marginBottom: 12 }}>
        Default Assumptions
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32 }}>
        <div>
          <label style={labelStyle(t)}>Risk-Free Rate (%)</label>
          <input
            type="text"
            value={sbc.riskFreeRate}
            onChange={e => { setSbc(p => ({ ...p, riskFreeRate: e.target.value })); markDirty(); }}
            style={{ ...inputStyle(t), maxWidth: 140 }}
          />
        </div>
        <div>
          <label style={labelStyle(t)}>Expected Volatility (%)</label>
          <input
            type="text"
            value={sbc.volatility}
            onChange={e => { setSbc(p => ({ ...p, volatility: e.target.value })); markDirty(); }}
            style={{ ...inputStyle(t), maxWidth: 140 }}
          />
        </div>
        <div>
          <label style={labelStyle(t)}>Dividend Yield (%)</label>
          <input
            type="text"
            value={sbc.dividendYield}
            onChange={e => { setSbc(p => ({ ...p, dividendYield: e.target.value })); markDirty(); }}
            style={{ ...inputStyle(t), maxWidth: 140 }}
          />
        </div>
        <div>
          <label style={labelStyle(t)}>Expected Term (years)</label>
          <input
            type="text"
            value={sbc.expectedTerm}
            onChange={e => { setSbc(p => ({ ...p, expectedTerm: e.target.value })); markDirty(); }}
            style={{ ...inputStyle(t), maxWidth: 140 }}
          />
        </div>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!dirty}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: 6,
            background: dirty ? green : t.border,
            color: dirty ? '#fff' : t.textMuted,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: sans,
            cursor: dirty ? 'pointer' : 'default',
            transition: 'all 0.15s',
          }}
        >
          Save
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: green, fontFamily: sans, fontWeight: 500 }}>
            Settings saved
          </span>
        )}
      </div>
    </div>
  );
}
